import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/hooks/useTenant';
import { startOfDayInTz, endOfDayInTz, DEFAULT_TIMEZONE, formatDateInputInTz } from '@/utils/timezone';
import { CommissionReportData, TechCommissionSummary, JobCommissionBreakdown, OverrideIncomeItem } from '@/types/techCommissionReport';
import { fetchAllRows, fetchAllRowsIn } from '@/utils/fetchAllRows';

/**
 * techId: undefined = every technician (with override income); a string = only
 * that technician's own jobs, override income from other techs' jobs is left
 * out; null = no technician chosen yet, so the query stays idle.
 */
export const useTechCommissionReport = (
  startDate: Date,
  endDate: Date,
  timezone: string = DEFAULT_TIMEZONE,
  techId?: string | null
) => {
  const { company } = useTenant();
  return useQuery({
    queryKey: ['reports', 'tech-commission-report', startDate.toISOString(), endDate.toISOString(), timezone, techId ?? 'all'],
    queryFn: async () => {
      // Use configured timezone for date boundaries
      const start = startOfDayInTz(startDate, timezone).toISOString();
      const end = endOfDayInTz(endDate, timezone).toISOString();

      // Filter by job_date (editable business date) in configured timezone
      const startDateStr = formatDateInputInTz(startDate, timezone);
      const endDateStr = formatDateInputInTz(endDate, timezone);

      // 1. Fetch completed jobs with tech assignments
      const jobs = await fetchAllRows((from, to) => {
        let jobsQuery = supabase
          .from('jobs')
          .select(`
            id,
            job_number,
            customer_name,
            scheduled_date,
            job_date,
            invoice_total,
            revenue_before_gst,
            total_parts_cost,
            toll_cost,
            total_card_fees,
            assigned_tech_id,
            techs (
              id,
              name,
              commission_percentage
            )
          `)
          .eq('status', 'completed')
          .not('assigned_tech_id', 'is', null)
          .gte('job_date', startDateStr)
          .lte('job_date', endDateStr);
        if (techId) jobsQuery = jobsQuery.eq('assigned_tech_id', techId);
        return jobsQuery
          .order('job_date', { ascending: true })
          .order('id')
          .range(from, to);
      });

      // 2. Fetch override commissions where tech is the beneficiary (income received)
      const overrideIncome = techId
        ? []
        : await fetchAllRows((from, to) => supabase
        .from('job_commission_overrides')
        .select(`
          id,
          job_id,
          beneficiary_technician_id,
          source_technician_id,
          job_net_revenue,
          override_rate_percentage,
          override_amount,
          job_completion_date,
          jobs (
            job_number
          ),
          source_tech:techs!source_technician_id (
            id,
            name
          ),
          beneficiary_tech:techs!beneficiary_technician_id (
            id,
            name,
            commission_percentage
          )
        `)
        .gte('job_completion_date', startDateStr)
        .lte('job_completion_date', endDateStr)
        .order('id')
        .range(from, to));

      // If no jobs and no overrides, return null
      if ((!jobs || jobs.length === 0) && (!overrideIncome || overrideIncome.length === 0)) {
        return null;
      }

      const jobIds = jobs?.map(j => j.id) || [];

      // 3. Fetch all job parts with source tracking
      const allParts = await fetchAllRowsIn(jobIds, (ids, from, to) => supabase
        .from('job_parts')
        .select('id, company_id, job_id, part_name, part_cost, quantity, part_source, notes, created_at')
        .in('job_id', ids)
        .order('id')
        .range(from, to));

      // 4. Fetch all job payments
      const allPayments = await fetchAllRowsIn(jobIds, (ids, from, to) => supabase
        .from('job_payments')
        .select('id, company_id, job_id, amount, payment_method, payment_date, card_processing_fee, notes, recorded_by, created_at')
        .in('job_id', ids)
        .order('id')
        .range(from, to));

      // 5. Build tech map - include techs with jobs AND techs with override income
      const techMap = new Map<string, TechCommissionSummary>();

      // Helper to create empty tech summary
      const createEmptyTechSummary = (techId: string, techName: string, commissionRate: number): TechCommissionSummary => ({
        techId,
        techName,
        commissionRate,
        totalJobs: 0,
        totalRevenue: 0,
        totalNetProfit: 0,
        totalGrossCommission: 0,
        totalCashCollected: 0,
        totalTechPartsReimbursement: 0,
        totalPartsCost: 0,
        totalCardFees: 0,
        totalFinalCommission: 0,
        totalOverrideIncome: 0,
        overrideIncomeItems: [],
        grandTotalEarnings: 0,
        jobs: [],
        allPartsUsed: [],
        paymentSummary: { cash: 0, card: 0, bankTransfer: 0, total: 0 }
      });

      // Process jobs (techs who completed work)
      jobs?.forEach(job => {
        const techId = job.assigned_tech_id!;
        const techName = job.techs?.name || 'Unknown';
        const commissionRate = job.techs?.commission_percentage || 0;

        // Get parts for this job
        const jobParts = allParts?.filter(p => p.job_id === job.id) || [];
        const techParts = jobParts.filter(p => p.part_source === 'tech_personal');
        const techPartsReimbursement = techParts.reduce((sum, p) => sum + (p.part_cost * p.quantity), 0);
        const companyPartsCost = jobParts
          .filter(p => p.part_source === 'company_stock' || !p.part_source)
          .reduce((sum, p) => sum + (p.part_cost * p.quantity), 0);

        // Get payments for this job
        const paidPayments = allPayments?.filter(p => p.job_id === job.id) || [];
        const cashCollected = paidPayments
          .filter(p => p.payment_method === 'cash')
          .reduce((sum, p) => sum + p.amount, 0);
        const cardPayments = paidPayments
          .filter(p => p.payment_method === 'card')
          .reduce((sum, p) => sum + p.amount, 0);
        const bankTransferPayments = paidPayments
          .filter(p => p.payment_method === 'bank_transfer')
          .reduce((sum, p) => sum + p.amount, 0);

        const cardProcessingFee = job.total_card_fees || 0;

        // Calculate commission (net profit now includes card fee deduction)
        const netProfit = (job.revenue_before_gst || 0) - (job.total_parts_cost || 0) - (job.toll_cost || 0) - cardProcessingFee;
        const grossCommission = netProfit * (commissionRate / 100);
        const finalCommission = grossCommission - cashCollected + techPartsReimbursement;

        // Create job breakdown
        const jobBreakdown: JobCommissionBreakdown = {
          jobId: job.id,
          jobNumber: job.job_number,
          customerName: job.customer_name,
          closedDate: job.job_date || '',
          scheduledDate: job.scheduled_date || job.job_date || '',
          revenueIncGst: job.invoice_total || 0,
          revenueExGst: job.revenue_before_gst || 0,
          totalPartsCost: job.total_parts_cost || 0,
          companyPartsCost,
          techPartsCost: techPartsReimbursement,
          tollsCost: job.toll_cost || 0,
          cardProcessingFee,
          cashCollected,
          cardPayments,
          bankTransferPayments,
          totalPayments: paidPayments.reduce((sum, p) => sum + p.amount, 0),
          netProfit,
          techCommissionRate: commissionRate,
          grossCommission,
          finalCommission,
          partsBreakdown: jobParts.map(p => ({
            name: p.part_name,
            quantity: p.quantity,
            unitCost: p.part_cost,
            totalCost: p.part_cost * p.quantity,
            source: (p.part_source as 'company_stock' | 'tech_personal') || 'company_stock',
            notes: p.notes || undefined
          })),
          paymentsBreakdown: {
            cash: cashCollected,
            card: cardPayments,
            bankTransfer: bankTransferPayments
          }
        };

        // Add to tech summary
        if (!techMap.has(techId)) {
          techMap.set(techId, createEmptyTechSummary(techId, techName, commissionRate));
        }

        const techSummary = techMap.get(techId)!;
        techSummary.totalJobs++;
        techSummary.totalRevenue += jobBreakdown.revenueExGst;
        techSummary.totalNetProfit += netProfit;
        techSummary.totalGrossCommission += grossCommission;
        techSummary.totalCashCollected += cashCollected;
        techSummary.totalTechPartsReimbursement += techPartsReimbursement;
        techSummary.totalPartsCost += jobBreakdown.totalPartsCost;
        techSummary.totalCardFees += cardProcessingFee;
        techSummary.totalFinalCommission += finalCommission;
        techSummary.jobs.push(jobBreakdown);
        techSummary.paymentSummary.cash += cashCollected;
        techSummary.paymentSummary.card += cardPayments;
        techSummary.paymentSummary.bankTransfer += bankTransferPayments;
        techSummary.paymentSummary.total += jobBreakdown.totalPayments;
      });

      // Process override income (techs who receive overrides from other techs)
      overrideIncome?.forEach(override => {
        const beneficiaryId = override.beneficiary_technician_id;
        const beneficiaryName = (override.beneficiary_tech as any)?.name || 'Unknown';
        const beneficiaryRate = (override.beneficiary_tech as any)?.commission_percentage || 0;

        // Add beneficiary tech to map if not present
        if (!techMap.has(beneficiaryId)) {
          techMap.set(beneficiaryId, createEmptyTechSummary(beneficiaryId, beneficiaryName, beneficiaryRate));
        }

        const techSummary = techMap.get(beneficiaryId)!;

        // Add override income item
        const overrideItem: OverrideIncomeItem = {
          jobId: override.job_id,
          jobNumber: (override.jobs as any)?.job_number || 'Unknown',
          sourceTechId: override.source_technician_id,
          sourceTechName: (override.source_tech as any)?.name || 'Unknown',
          jobNetRevenue: override.job_net_revenue,
          overrideRatePercentage: override.override_rate_percentage,
          overrideAmount: override.override_amount,
          jobCompletionDate: override.job_completion_date
        };

        techSummary.overrideIncomeItems.push(overrideItem);
        techSummary.totalOverrideIncome += override.override_amount;
      });

      // Calculate grand total and aggregate parts for each tech
      techMap.forEach(techSummary => {
        // Grand total = job commission + override income
        techSummary.grandTotalEarnings = techSummary.totalFinalCommission + techSummary.totalOverrideIncome;

        // Aggregate parts
        const partsMap = new Map<string, any>();
        techSummary.jobs.forEach(job => {
          job.partsBreakdown.forEach(part => {
            const key = `${part.name}_${part.source}`;
            if (!partsMap.has(key)) {
              partsMap.set(key, {
                partName: part.name,
                totalQuantity: 0,
                totalCost: 0,
                source: part.source
              });
            }
            const agg = partsMap.get(key);
            agg.totalQuantity += part.quantity;
            agg.totalCost += part.totalCost;
          });
        });
        techSummary.allPartsUsed = Array.from(partsMap.values());
      });

      const technicians = Array.from(techMap.values()).sort((a, b) => a.techName.localeCompare(b.techName));

      const reportData: CommissionReportData = {
        startDate: start,
        endDate: end,
        generatedAt: new Date().toISOString(),
        companyName: company?.name || 'Company',
        technicians,
        overallTotals: {
          totalTechnicians: techMap.size,
          totalJobs: jobs?.length || 0,
          totalRevenue: technicians.reduce((sum, t) => sum + t.totalRevenue, 0),
          totalCommissionPaid: technicians.reduce((sum, t) => sum + t.totalFinalCommission, 0),
          totalOverridesPaid: technicians.reduce((sum, t) => sum + t.totalOverrideIncome, 0)
        }
      };

      return reportData;
    },
    enabled: !!startDate && !!endDate && techId !== null,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
};
